import React, { FC, ReactNode, useEffect } from "react";
import { Heading, Text, Button, Switch, SkeletonBase, toast } from "@foris/avocado-suite";
import DimensionsSearchSkeleton from "@components/dimensions-search-skeleton";
import { Header } from "@common/components";
import scss from "./changesHistoryResults.module.scss";

/**
 * Reusable container component for displaying change history data
 *
 * Provides a consistent layout with header, loading state, filters, and action buttons
 * Used by multiple history views across the application to maintain UI consistency
 */
interface ChangesHistoryResultsProps {
  title: string;
  subtitle?: string;
  loading: boolean;
  labelSwitch: string;
  breadcrumbItems: any[];
  /** Optional flag to indicate if initial data has been loaded */
  hasInitialLoad?: boolean;
  lastUpdateDateTime: string;
  /** Callback to refresh data */
  handleUpdateView: () => void;
  /** Callback to generate and download report */
  handleDownloadReport: () => void;
  labelLastUpdate: string;
  /** Whether to show only the current user's changes */
  showSelfChanges: boolean;
  /** Callback to toggle the self-changes filter */
  setShowSelfChanges: (value: boolean) => void;
  buttonDownloadReportLabel: string;
  buttonUpdateViewLabel: string;
  children: ReactNode;
  columns?: any[];
  rows?: number;
  showSnackbar?: boolean;
  setShowSnackbar?: (value: boolean) => void;
  titleSnackbar?: string;
  filters?: ReactNode;
}

/**
 * A layout wrapper for change history views that provides:
 * - Consistent header with breadcrumb navigation
 * - Loading indicator during data fetches
 * - Last update timestamp display
 * - Action buttons for refreshing and exporting data
 * - Filter toggle for showing only the current user's changes
 */
const ChangesHistoryResults: FC<ChangesHistoryResultsProps> = ({
  title,
  subtitle,
  loading,
  labelSwitch,
  breadcrumbItems,
  lastUpdateDateTime,
  handleDownloadReport,
  handleUpdateView,
  labelLastUpdate,
  showSelfChanges,
  setShowSelfChanges,
  buttonDownloadReportLabel,
  buttonUpdateViewLabel,
  children,
  columns,
  rows,
  showSnackbar,
  setShowSnackbar,
  titleSnackbar,
  filters,
}) => {
  useEffect(() => {
    if (showSnackbar) {
      setShowSnackbar(false);

      toast.success(titleSnackbar, {
        autoClose: 5000,
      });
    }
  }, [showSnackbar]);

  return (
    <div>
      <Header
        title={loading ? <SkeletonBase height={24} maxWidth={300} /> : (title ?? "")}
        breadcrumbItems={breadcrumbItems}
      />
      {loading ? (
        <div className={scss.changesHistoryResults}>
          <DimensionsSearchSkeleton columns={columns?.length} rows={rows} />{" "}
        </div>
      ) : (
        <div className={scss.changesHistoryResults}>
          <header className={scss.changesHistoryResults_header}>
            {!!subtitle && <Heading type="h2">{subtitle}</Heading>}
            <div className={scss.container}>
              <Text weight="medium" className={scss.container_label}>
                {labelLastUpdate}: <span className={scss.textHour}>{lastUpdateDateTime}</span>
              </Text>
              <div className={scss.container_buttonGroup}>
                <Button size="sm" variant="ghost" leftIcon="refresh" onClick={handleUpdateView}>
                  {buttonUpdateViewLabel}
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  leftIcon="download"
                  onClick={handleDownloadReport}
                >
                  {buttonDownloadReportLabel}
                </Button>
              </div>
            </div>
          </header>

          <div className={scss.changesHistoryResults_filters}>
            {filters}
            <Switch
              className={scss.switch}
              label={labelSwitch}
              checked={showSelfChanges}
              onChange={() => {
                setShowSelfChanges(!showSelfChanges);
              }}
            />
          </div>
          {children}
        </div>
      )}
    </div>
  );
};

export default ChangesHistoryResults;
